/**
 * WeeklyPlanContainer — data-fetching container for the Plan Semanal tab.
 *
 * Mirrors TrackingContainer pattern:
 *   - Uses useWeeklyPlan hook for data fetching and generation
 *   - Renders loading / error / empty / plan states explicitly
 *   - aria-live="polite" region for async status announcements
 *   - No native dialogs (no alert(), confirm(), prompt())
 *   - No dangerouslySetInnerHTML
 *
 * Responsive: single column, max-w-2xl matches App shell container.
 */

import { useWeeklyPlan } from "./useWeeklyPlan.js";
import { WeeklyPlanView } from "./WeeklyPlanView.js";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface WeeklyPlanContainerProps {
  workerUrl: string;
}

// ---------------------------------------------------------------------------
// WeeklyPlanContainer
// ---------------------------------------------------------------------------

export function WeeklyPlanContainer({ workerUrl }: WeeklyPlanContainerProps): JSX.Element {
  const { plan, loading, error, generating, generateError, generate } = useWeeklyPlan(workerUrl);

  return (
    <div className="flex flex-col gap-4">
      {/* aria-live region for async status (loading, error, empty) */}
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {loading ? "Cargando plan semanal…" : null}
        {generating ? "Generando plan semanal…" : null}
        {error !== undefined ? `Error: ${error}` : null}
        {!loading && error === undefined && plan === null
          ? "No tenés un plan semanal generado aún."
          : null}
      </div>

      {loading ? <LoadingState /> : null}

      {!loading && error !== undefined ? <ErrorState message={error} /> : null}

      {!loading && error === undefined && plan === null ? (
        <EmptyState
          generating={generating}
          onGenerate={() => { void generate(); }}
          {...(generateError !== undefined ? { generateError } : {})}
        />
      ) : null}

      {!loading && error === undefined && plan !== null ? (
        <WeeklyPlanView plan={plan} />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Loading state
// ---------------------------------------------------------------------------

function LoadingState(): JSX.Element {
  return (
    <div
      role="status"
      aria-label="Cargando plan semanal"
      className="flex flex-col gap-3 rounded-xl border border-zinc-700 bg-zinc-900 p-6"
    >
      <div className="h-6 w-48 animate-pulse rounded bg-zinc-700" />
      <div className="h-4 w-full animate-pulse rounded bg-zinc-800" />
      <div className="h-4 w-3/4 animate-pulse rounded bg-zinc-800" />
      <div className="h-4 w-2/3 animate-pulse rounded bg-zinc-800" />
      <span className="sr-only">Cargando plan semanal…</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Error state
// ---------------------------------------------------------------------------

function ErrorState({ message }: { message: string }): JSX.Element {
  return (
    <div
      role="alert"
      aria-live="assertive"
      className="rounded-xl border border-red-800/60 bg-red-950/20 px-4 py-3 text-sm text-red-400"
    >
      {message}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Empty state
// ---------------------------------------------------------------------------

interface EmptyStateProps {
  generating: boolean;
  generateError?: string;
  onGenerate: () => void;
}

function EmptyState({ generating, generateError, onGenerate }: EmptyStateProps): JSX.Element {
  return (
    <div className="flex flex-col items-center gap-4 rounded-xl border border-zinc-700 bg-zinc-900 p-8 text-center">
      <p className="text-sm text-zinc-400">
        No tenés un plan semanal generado todavía.
      </p>
      <p className="text-xs text-zinc-500">
        Completá tu perfil con tus actividades semanales y generá tu plan personalizado.
      </p>

      {generateError !== undefined ? (
        <div
          role="alert"
          aria-live="assertive"
          className="w-full rounded-lg border border-red-800/60 bg-red-950/20 px-4 py-3 text-sm text-red-400"
        >
          {generateError}
        </div>
      ) : null}

      <button
        type="button"
        className="min-h-[44px] rounded-lg bg-orange-600 px-6 text-sm font-semibold text-white transition-colors hover:bg-orange-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 disabled:cursor-not-allowed disabled:opacity-60"
        aria-label={generating ? "Generando plan semanal…" : "Generar plan semanal"}
        aria-busy={generating}
        disabled={generating}
        onClick={onGenerate}
      >
        {generating ? (
          <span role="status" className="flex items-center gap-2">
            <svg
              aria-hidden="true"
              className="h-4 w-4 animate-spin"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
              />
            </svg>
            Generando…
          </span>
        ) : (
          "Generar plan semanal"
        )}
      </button>
    </div>
  );
}
